import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DUMMY_BUMDES, DUMMY_PO_LIST } from './mocks/dplDummyData';

const StatusBadge = ({ status }) =>
  status === 'attention' ? (
    <span className='badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold d-inline-flex align-items-center gap-1'>
      <i className='bi bi-exclamation-triangle-fill'></i> Perlu Perhatian
    </span>
  ) : (
    <span className='badge bg-success bg-opacity-10 text-success border border-success border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold'>
      Aktif
    </span>
  );

const BMCBadge = ({ status }) => {
  const map = { Disetujui: 'success', Direvisi: 'warning', Draft: 'secondary' };
  const color = map[status] || 'secondary';
  return (
    <span
      className={`badge bg-${color} bg-opacity-10 text-${color} border border-${color} border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold`}
    >
      {status}
    </span>
  );
};

const DPLDashboard = () => {
  const { authUser } = useAuth();
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () =>
      DUMMY_PO_LIST.filter(
        (po) =>
          po.name.toLowerCase().includes(query.toLowerCase()) ||
          po.university.toLowerCase().includes(query.toLowerCase()),
      ),
    [query],
  );

  const openEscalations = DUMMY_PO_LIST.flatMap((po) =>
    po.escalations.filter((e) => e.status === 'open'),
  );
  const attentionCount = DUMMY_PO_LIST.filter(
    (p) => p.status === 'attention',
  ).length;

  const getBMCContribution = (poName) => {
    const versions = DUMMY_BUMDES.bmc.history.filter((h) =>
      h.updatedBy.includes(poName),
    );
    if (versions.length === 0)
      return <span className='text-muted small'>—</span>;
    const latestVer = versions[0].version;
    return (
      <span className='badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold'>
        v{latestVer} ({versions.length}x)
      </span>
    );
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 mb-5'>
      {/* ── Dashboard Header ── */}
      <div className='d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3 flex-shrink-0'
            style={{ width: 48, height: 48 }}
          >
            <i className='bi bi-shop-window text-primary fs-4'></i>
          </div>
          <div>
            <div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
              <h1 className='h5 fw-bold text-dark mb-0'>{DUMMY_BUMDES.name}</h1>
              <span className='badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 rounded-pill extra-small fw-bold px-2 py-1'>
                {DUMMY_BUMDES.batch}
              </span>
            </div>
            <div
              className='d-flex align-items-center gap-1 text-muted'
              style={{ fontSize: '0.8rem' }}
            >
              <i className='bi bi-geo-alt-fill text-primary opacity-50 small'></i>
              {DUMMY_BUMDES.region}
            </div>
          </div>
        </div>
        <div className='d-flex flex-wrap gap-3 small text-muted align-items-center'>
          <div className='d-flex align-items-center gap-1'>
            <i className='bi bi-calendar-event text-primary opacity-50'></i>
            <span>
              Mulai:{' '}
              <strong className='text-dark'>
                {DUMMY_BUMDES.programStartDate}
              </strong>
            </span>
          </div>
          <div className='d-flex align-items-center gap-1'>
            <i className='bi bi-calendar-check text-primary opacity-50'></i>
            <span>
              Selesai:{' '}
              <strong className='text-dark'>
                {DUMMY_BUMDES.programEndDate}
              </strong>
            </span>
          </div>
          <span
            className='badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill fw-semibold'
            style={{ fontSize: '0.75rem' }}
          >
            <i className='bi bi-eye me-1'></i>View Only
          </span>
        </div>
      </div>

      {/* ── Stat Widgets Row ── */}
      <div className='row g-3 mb-4'>
        {[
          {
            label: 'Total PO Binaan',
            value: DUMMY_PO_LIST.length,
            icon: 'bi-people-fill',
            color: '#4f46e5',
          },
          {
            label: 'Perlu Perhatian',
            value: attentionCount,
            icon: 'bi-exclamation-triangle-fill',
            color: '#ef4444',
          },
          {
            label: 'Eskalasi Terbuka',
            value: openEscalations.length,
            icon: 'bi-megaphone-fill',
            color: '#f59e0b',
          },
          {
            label: 'VECI T0 Lembaga',
            value: DUMMY_BUMDES.veci.t0.score
              ? `${DUMMY_BUMDES.veci.t0.score.toFixed(1)} / 50`
              : '—',
            icon: 'bi-clipboard-data-fill',
            color: '#0891b2',
          },
          {
            label: 'VECI T1 Lembaga',
            value: DUMMY_BUMDES.veci.t1.score
              ? `${DUMMY_BUMDES.veci.t1.score.toFixed(1)} / 50`
              : '—',
            icon: 'bi-clipboard-check-fill',
            color: '#10b981',
          },
        ].map((s) => (
          <div
            className='col-6 col-md'
            key={s.label}
          >
            <div className='bg-light rounded-3 p-3 d-flex align-items-center gap-3 h-100 border border-light-subtle'>
              <div
                className='rounded-3 d-flex align-items-center justify-content-center flex-shrink-0'
                style={{ width: 36, height: 36, background: `${s.color}18` }}
              >
                <i
                  className={`bi ${s.icon} small`}
                  style={{ color: s.color }}
                ></i>
              </div>
              <div>
                <div
                  className='fw-bold text-dark'
                  style={{ fontSize: '1.1rem' }}
                >
                  {s.value}
                </div>
                <div
                  className='text-muted'
                  style={{ fontSize: '0.7rem' }}
                >
                  {s.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <hr className='my-4 opacity-25' />

      {/* ── PO Table Header ── */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: 40, height: 40 }}
          >
            <i className='bi bi-people text-primary fs-5'></i>
          </div>
          <div>
            <h2 className='h5 fw-bold mb-0 text-dark'>
              Daftar Project Officer
            </h2>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Ringkasan status seluruh PO yang bertugas di {DUMMY_BUMDES.name}.
            </p>
          </div>
        </div>
        <span className='text-muted small ms-auto'>
          {filtered.length} dari {DUMMY_PO_LIST.length} PO
        </span>
      </div>

      {/* ── Filter Toolbar ── */}
      <div className='row g-3 mb-4 align-items-center'>
        <div className='col-md-6 col-lg-8'>
          <div className='input-group input-group-sm bg-light rounded-pill px-3 py-2 border-0 shadow-none'>
            <span className='input-group-text bg-transparent border-0 text-muted'>
              <i className='bi bi-search'></i>
            </span>
            <input
              type='text'
              className='form-control bg-transparent border-0 shadow-none'
              placeholder='Cari nama PO atau universitas...'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>
        </div>
      </div>

      {/* ── PO Table ── */}
      <div className='table-responsive'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th
                className='ps-4 py-3 border-0'
                style={{ width: 40 }}
              >
                #
              </th>
              <th className='border-0 py-3'>Project Officer</th>
              <th className='border-0 py-3'>Universitas</th>
              <th className='border-0 py-3'>Kontak / Email</th>
              <th className='border-0 py-3 text-center'>Kontribusi BMC</th>
              <th className='border-0 py-3 text-center'>One Pager</th>
              <th className='border-0 py-3 text-center'>Eskalasi</th>
              <th className='border-0 py-3 text-center'>Aktivitas Terakhir</th>
              <th className='border-0 py-3 text-center'>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((po, idx) => {
                const submittedCount = po.onePager.filter(
                  (m) => m.submitted,
                ).length;
                return (
                  <tr
                    key={po.id}
                    className='cursor-pointer transition-all'
                  >
                    <td className='ps-4 py-3 text-muted small'>{idx + 1}</td>
                    <td className='py-3'>
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0'
                          style={{
                            width: 34,
                            height: 34,
                            background: po.color,
                            fontSize: '0.85rem',
                          }}
                        >
                          {po.avatar}
                        </div>
                        <div>
                          <div className='fw-bold text-dark'>{po.name}</div>
                          <div className='text-muted extra-small'>
                            {po.faculty}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='py-3'>
                      <div className='small text-dark fw-medium'>
                        {po.university}
                      </div>
                    </td>
                    <td className='py-3'>
                      <div className='small text-dark fw-medium'>
                        {po.phone}
                      </div>
                      <div className='text-muted extra-small'>{po.email}</div>
                    </td>
                    <td className='py-3 text-center'>
                      {getBMCContribution(po.name)}
                    </td>
                    <td className='py-3 text-center'>
                      <span className='badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold'>
                        {submittedCount}/{po.onePager.length}
                      </span>
                    </td>
                    <td className='py-3 text-center'>
                      {po.escalations.length > 0 ? (
                        <span className='badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-10 rounded-pill extra-small fw-bold px-2 py-1'>
                          {po.escalations.length} open
                        </span>
                      ) : (
                        <i className='bi bi-check-circle-fill text-success'></i>
                      )}
                    </td>
                    <td className='py-3 text-center'>
                      <span className='text-muted small'>
                        {po.lastActivity}
                      </span>
                    </td>
                    <td className='py-3 text-center'>
                      <StatusBadge status={po.status} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className='text-center py-5'
                >
                  <div className='text-muted mb-3'>
                    <i className='bi bi-people fs-1 opacity-25'></i>
                  </div>
                  <h6 className='fw-bold text-dark mb-1'>Tidak Ada Data</h6>
                  <p className='text-muted small mb-0'>
                    Tidak ada PO yang sesuai dengan pencarian Anda.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .transition-all { transition: all 0.2s ease; }
        .cursor-pointer { cursor: pointer; }
        .extra-small { font-size: 0.72rem; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default DPLDashboard;
